import { useState } from "react";
import { Combobox } from "@headlessui/react";
import { ChevronIcon, XIcon } from "@/assets/icons";
import FormError from "./FormError";
import { useTranslation } from "react-i18next";
import { useDebounce } from "@/shared/hooks";

interface Classifier {
  code: string;
  title: string;
}

interface FormComboBoxProps {
  label: string;
  items: Classifier[];
  value: string | null;
  onChange: (code: string | null) => void;
  onBlur?: () => void;
  className?: string;
  disabled?: boolean;
  readOnly?: boolean;
  error?: string;
  errorParams?: Record<string, string | number>;
  required?: boolean;
  isLoading?: boolean;
  isClearable?: boolean;
}

export default function FormComboBox({
  label,
  items,
  value,
  onChange,
  onBlur,
  className = "",
  disabled = false,
  readOnly = false,
  error,
  errorParams,
  required = false,
  isLoading = false,
  isClearable = true,
}: FormComboBoxProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query, 200);

  const selectedItem = items.find(item => item.code === value) || null;

  const handleSelect = (item: Classifier | null) => {
    onChange(item ? item.code : null);
    setQuery("");
  };

  const handleClear = (e: React.MouseEvent) => {
    if (disabled || readOnly || isLoading) return;
    e.preventDefault();
    e.stopPropagation();
    onChange(null);
    setQuery("");
  };

  const filteredItems = debouncedQuery
    ? items.filter(
        item =>
          item.title.toLowerCase().includes(debouncedQuery.toLowerCase()) ||
          item.code.toLowerCase().includes(debouncedQuery.toLowerCase())
      )
    : items;

  const isInputDisabled = disabled || isLoading || readOnly;

  return (
    <div className={className}>
      <Combobox
        value={selectedItem}
        onChange={handleSelect}
        disabled={isInputDisabled}
        immediate
      >
        {({ open }) => (
          <>
            <label className="block text-sm font-medium text-black mb-2">
              {label}
              {required && <span className="text-black ml-1">*</span>}
            </label>
            <div className="relative">
              <Combobox.Input
                className={`w-full py-2 text-sm bg-white border rounded-md focus:outline-none transition-colors duration-200 truncate ${
                  readOnly ? "px-3" : value ? "pl-3 pr-12" : "pl-3 pr-8"
                } ${
                  readOnly
                    ? "border-gray-200 bg-gray-50 focus:border-gray-200 focus:ring-0 focus:outline-none cursor-default"
                    : error
                      ? "border-red-500 focus:ring-2 focus:ring-red-500 focus:border-red-500 cursor-text"
                      : "border-gray-300 hover:border-gray-400 focus:ring-2 focus:ring-primary focus:border-primary cursor-text"
                } ${isLoading ? "bg-gray-50 cursor-default" : ""} ${
                  disabled ? "opacity-50 cursor-not-allowed bg-gray-50" : ""
                }`}
                onChange={event => setQuery(event.target.value)}
                onBlur={readOnly ? undefined : onBlur}
                onFocus={event => event.target.select()}
                autoComplete="off"
                readOnly={readOnly}
                displayValue={(item: Classifier) =>
                  isLoading
                    ? t("common.loading")
                    : item
                      ? `${item.code} - ${item.title}`
                      : ""
                }
              />
              {!readOnly && (
                <div className="absolute inset-y-0 right-0 flex items-center pr-2 gap-1">
                  {isClearable && value && !isInputDisabled && (
                    <button
                      type="button"
                      onClick={handleClear}
                      className="p-1 text-gray-400 hover:text-red-500 transition-colors duration-200"
                    >
                      <XIcon size={14} />
                    </button>
                  )}
                  <Combobox.Button
                    disabled={isInputDisabled}
                    className="p-1 transition-colors duration-200 group disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronIcon
                      size={16}
                      className={`text-gray-400 group-hover:text-gray-600 transform transition-transform duration-200 ${
                        open ? "rotate-180" : ""
                      }`}
                      direction="down"
                    />
                  </Combobox.Button>
                </div>
              )}
              <Combobox.Options className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                {filteredItems.length === 0 && query !== "" ? (
                  <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                    {t("common.nothingFound")}
                  </div>
                ) : (
                  filteredItems.map(item => (
                    <Combobox.Option
                      key={item.code}
                      value={item}
                      className={({ active }) =>
                        `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? "bg-primary text-white" : "text-gray-900"}`
                      }
                    >
                      {({ selected }) => (
                        <>
                          <span
                            className={`block truncate ${selected ? "font-medium" : "font-normal"}`}
                          >
                            {item.code} - {item.title}
                          </span>
                        </>
                      )}
                    </Combobox.Option>
                  ))
                )}
              </Combobox.Options>
            </div>
            <FormError error={error} params={errorParams} />
          </>
        )}
      </Combobox>
    </div>
  );
}
